import React, { memo, createContext, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Stepper as MuiStepper,
  unstable_composeClasses as composeClasses,
  generateUtilityClass,
  generateUtilityClasses,
  useThemeProps,
} from '@mui/material';
import clsx from 'clsx';
import { capitalize } from 'lodash';

const PREFIX = 'MuiStepper';

export function getStepIconUtilityClass(slot) {
  return generateUtilityClass(PREFIX, slot);
}

const useUtilityClasses = (ownerState) => {
  const { color, classes } = ownerState;

  const slots = {
    root: ['root', `color${capitalize(color)}`],
  };

  return composeClasses(slots, getStepIconUtilityClass, classes);
};

const stepIconClasses = generateUtilityClasses(PREFIX, [
  'colorPrimary',
  'colorSecondary',
  'colorInfo',
  'colorSuccess',
  'colorWarning',
  'colorError',
]);

export const StepperContext = createContext();

const StyledStepper = styled(MuiStepper)(({ theme }) => ({
  [`&.${stepIconClasses.colorPrimary}`]: {
    color: theme.palette.primary.main,
  },

  [`&.${stepIconClasses.colorSecondary}`]: {
    color: theme.palette.secondary.main,
  },

  [`&.${stepIconClasses.colorInfo}`]: {
    color: theme.palette.info.main,
  },

  [`&.${stepIconClasses.colorSuccess}`]: {
    color: theme.palette.success.main,
  },

  [`&.${stepIconClasses.colorWarning}`]: {
    color: theme.palette.warning.main,
  },

  [`&.${stepIconClasses.colorError}`]: {
    color: theme.palette.error.main,
  },
}));

const Stepper = memo(function Stepper(inProps) {
  /**
   * MUI
   */
  const props = useThemeProps({ props: inProps, name: PREFIX });

  const classes = useUtilityClasses(props);

  const { children, color = 'primary', className, ...rest } = props;

  const contextValue = useMemo(() => {
    return {
      color,
    };
  }, [color]);

  return (
    <StepperContext.Provider value={contextValue}>
      <StyledStepper
        {...rest}
        className={clsx(
          className,
          classes[`${PREFIX}-color${capitalize(color)}`],
        )}
        classes={classes}
      >
        {children}
      </StyledStepper>
    </StepperContext.Provider>
  );
});

Stepper.propTypes = {
  ...MuiStepper.propTypes,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default Stepper;
